
/* ############################################################################################## */
/* ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
/*                                              Config                                            */
/* ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

/* Shop-Config */
import shopConfig from 'template/config'
import type { ContactModalPalette } from 'src/components/ContactModal/palette'
import { resolveCartVariant, type CartVariant } from 'src/functions/cartVariant'
import { resolveCheckoutVariant, type CheckoutVariant } from 'src/functions/checkoutVariant'
import { resolveMobileMenuVariant, type MobileMenuVariant } from 'src/functions/mobileMenuVariant'

/* Types */
import { PayPalScriptOptions } from '@paypal/paypal-js/types/script-options'

type PageBuilderDefaults = {
  layout?: {
    stylePreset?: 'individual' | 'westwing'
    navigationPosition?: 'side' | 'top'
    navigationDisplay?: 'none' | 'separate' | 'inline'
    heroMobileMode?: 'content' | 'fullscreen'
    contentWidth?: 'contained' | 'wide' | 'full'
    magazinePlacement?: 'afterHero' | 'afterFilters' | 'beforeProducts' | 'afterProducts' | 'hidden'
    hideLegacyHeader?: boolean
    showCategorySliderArrows?: boolean
  }
  theme?: {
    fontFamily?: string
    backgroundColor?: string
    surfaceColor?: string
    textColor?: string
    mutedTextColor?: string
    accentColor?: string
    accentContrastColor?: string
    buttonBackgroundColor?: string
    buttonHoverColor?: string
    buttonTextColor?: string
    purchaseButtonBackgroundColor?: string
    purchaseButtonHoverColor?: string
    purchaseButtonTextColor?: string
    infoColor?: string
    successColor?: string
    warningColor?: string
    errorColor?: string
    navigationBackgroundColor?: string
    navigationTextColor?: string
    navigationOverlayOpacity?: number
  }
  products?: {
    columnsPerRow?: number
    columnsPerRowMobile?: number
  }
}

type HeaderLogoConfig = {
  builderMaxWidth?: number | string
  builderMaxHeight?: number | string
  builderInlineMaxWidth?: number | string
  builderMobileMaxWidth?: number | string
  builderMobileMaxHeight?: number | string
}

type ProductPreviewConfig = {
  mobileStyle?: 'default' | 'editorial'
}

type ProductImageViewerConfig = {
  variant?: 'default' | 'fullscreen'
}

type PageBuilderImagePresentation = {
  srcSet?: string
  brandId: number
  productId?: number
  logoSrc?: string
  logoAlt?: string
  logoOverlay?: boolean
  width?: number
  height?: number
  logoEmbedded?: boolean
}

const defaultVersandkosten = {
  deutschland: 0,
  spedition: 89,
  speditionAusland: {} as Record<string, number>,
  dhlZohne1: 20,
  dhlZohne2: 25,
  dhlZohne3: 30,
  ausserhalbEuropa: 50,
}

type StructuredDataConfig = {
  returns: {
    merchantReturnDays: number
    returnFees: string
    returnMethod: string
    refundType: string
    applicableCountries?: readonly string[]
    returnPolicyCountry?: string
  }
  shipping: {
    destinationCountry: string
    handlingTime: { minValue: number, maxValue: number, unitCode: string }
    shippingTypes: Record<number, { name: string, rate: keyof typeof defaultVersandkosten }>
    defaultShippingType: number
  }
}

const dev = (process.env.NODE_ENV === 'development')
const paymentConfig = shopConfig as typeof shopConfig & {
  VorkasseDisplay?: boolean
  NachnahmeDisplay?: boolean
  RechnungDisplay?: boolean
  PayPalDisplay?: boolean
  PayPalAdvancedCardsDisplay?: boolean
  PayPalGooglePayDisplay?: boolean
  PayPalApplePayDisplay?: boolean
  PayPalPayLaterDisplay?: boolean
  DemoPaymentDisplay?: boolean
  siteId?: number
  siteURL?: string
  stockDeliveryTime?: number
  deliveryCountries?: readonly string[]
  versandkosten?: Partial<typeof defaultVersandkosten>
  pageBuilderDefaults?: PageBuilderDefaults
  spaciousBrandCarousels?: boolean
  contactModalPalette?: ContactModalPalette
  pageBuilderImagePresentation?: (src: string) => PageBuilderImagePresentation | undefined
  headerLogo?: HeaderLogoConfig
  productPreview?: ProductPreviewConfig
  productImageViewer?: ProductImageViewerConfig
  productStickyPurchase?: boolean
  cartVariant?: CartVariant
  mobileMenuVariant?: MobileMenuVariant
  checkoutVariant?: CheckoutVariant
  compactCheckoutFooter?: boolean
  footerMobileAccordions?: boolean
  productInquiry?: {
    buttonLabel?: string
    cartInquiryButtonLabel?: string
    inquiryOnlyButtonLabel?: string
    buttonBackgroundColor?: string
    buttonHoverColor?: string
    buttonTextColor?: string
    availabilityLabel?: string
    detailNotice?: string
    onlyInquiryNotice?: string
    selectionHint?: string
  }
}
const readEnv = (name: string, fallback: string) => {
  const value = process.env[name]
  return typeof value === 'string' && value.trim() !== ''
    ? value.trim()
    : fallback
}

const normalizeDeliveryDays = (value: unknown, fallback: number) => {
  const days = Number(value)
  return Number.isFinite(days) && days > 0
    ? Math.round(days)
    : fallback
}

const shopVersandkosten = paymentConfig.versandkosten || {}
const deliveryCountries = Array.isArray(paymentConfig.deliveryCountries)
  ? paymentConfig.deliveryCountries
    .map(country => String(country).trim().toUpperCase())
    .filter(country => /^[A-Z]{2}$/.test(country))
  : []
const structuredData = shopConfig.structuredData as StructuredDataConfig
const versandkosten = {
  ...defaultVersandkosten,
  ...shopVersandkosten,
  speditionAusland: {
    ...defaultVersandkosten.speditionAusland,
    ...(shopVersandkosten.speditionAusland || {}),
  },
}

const basePaypalConfig = shopConfig.paypal[dev ? 'dev' : 'live']
const paypalSandboxClientId = process.env.NEXT_PUBLIC_PAYPAL_SANDBOX_CLIENT_ID
const paypalClientId = dev
  ? (typeof paypalSandboxClientId === 'string' && paypalSandboxClientId.trim() !== ''
      ? paypalSandboxClientId.trim()
      : String(basePaypalConfig['client-id'] || ''))
  : String(basePaypalConfig['client-id'] || '')

const paypalConfig: PayPalScriptOptions = {
  ...basePaypalConfig,
  ...(paypalClientId ? { ['client-id']: paypalClientId } : {}),
  debug: false, // dev,
  locale: 'de_DE',
  currency: 'EUR',
  vault: false,
  commit: true,
  components: [
    'buttons',
    'marks',
    'messages',
    paymentConfig.PayPalGooglePayDisplay ? 'googlepay' : undefined,
    paymentConfig.PayPalApplePayDisplay ? 'applepay' : undefined,
  ].filter(Boolean).join(','),
  ...(paymentConfig.PayPalPayLaterDisplay
    ? { ['enable-funding']: 'paylater' }
    : {}),
  ['integration-date']: '2022-08-16',
} as const

const productInquiryConfig = {
  buttonLabel: paymentConfig.productInquiry?.buttonLabel || 'Angebot anfordern',
  cartInquiryButtonLabel: paymentConfig.productInquiry?.cartInquiryButtonLabel
    || paymentConfig.productInquiry?.buttonLabel
    || 'Angebot anfordern',
  inquiryOnlyButtonLabel: paymentConfig.productInquiry?.inquiryOnlyButtonLabel
    || paymentConfig.productInquiry?.buttonLabel
    || 'Angebot anfordern',
  buttonBackgroundColor: paymentConfig.productInquiry?.buttonBackgroundColor || '#fba466',
  buttonHoverColor: paymentConfig.productInquiry?.buttonHoverColor || '#f39355',
  buttonTextColor: paymentConfig.productInquiry?.buttonTextColor || '#2f2d27',
  availabilityLabel: paymentConfig.productInquiry?.availabilityLabel || 'Nur auf Anfrage',
  detailNotice: paymentConfig.productInquiry?.detailNotice || 'Dieser Artikel ist nur auf Anfrage erhältlich. Der Preis bleibt sichtbar, die Bestellung erfolgt aber nicht direkt online.',
  onlyInquiryNotice: paymentConfig.productInquiry?.onlyInquiryNotice || 'Dieser Artikel kann aktuell nicht direkt online bestellt werden. Nutzen Sie stattdessen die Anfrage.',
  selectionHint: paymentConfig.productInquiry?.selectionHint || 'Bitte wählen Sie erst alle Varianten aus, bevor Sie die Anfrage senden.',
} as const

export default {

  dev,

  /* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
  /*                                  Shop                                 */
  /* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

  mwst: 19,

  mindermengenzuschlag: {
    entfaelltAb: 79,
    betrag: 5,
  },

  versandkosten,

  deliveryCountries,

  stockDeliveryTime: normalizeDeliveryDays(paymentConfig.stockDeliveryTime, 3), // days

  structuredData,

  shop: {
    shop: Number(process.env.NEXT_PUBLIC_SHOP_ID),
    lang: process.env.NEXT_PUBLIC_LANGUAGE,
  },

  couponDisplay: shopConfig.couponDisplay,

  siteId: paymentConfig.siteId,
  siteURL: paymentConfig.siteURL,
  pageBuilderDefaults: paymentConfig.pageBuilderDefaults,
  spaciousBrandCarousels: paymentConfig.spaciousBrandCarousels === true,
  contactModalPalette: paymentConfig.contactModalPalette,
  pageBuilderImagePresentation: paymentConfig.pageBuilderImagePresentation as
    ((src: string) => PageBuilderImagePresentation | undefined) | undefined,
  headerLogo: paymentConfig.headerLogo,
  productPreview: paymentConfig.productPreview,
  productImageViewer: paymentConfig.productImageViewer,
  productStickyPurchase: paymentConfig.productStickyPurchase === true,
  cartVariant: resolveCartVariant(paymentConfig.cartVariant),
  mobileMenuVariant: resolveMobileMenuVariant(paymentConfig.mobileMenuVariant),
  checkoutVariant: resolveCheckoutVariant(paymentConfig.checkoutVariant),
  compactCheckoutFooter: paymentConfig.compactCheckoutFooter === true,
  footerMobileAccordions: paymentConfig.footerMobileAccordions === true,
  
  /* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
  /*                           Zahlungsvarianten                           */
  /* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

    VorkasseDisplay: paymentConfig.VorkasseDisplay ?? false,
    NachnahmeDisplay: paymentConfig.NachnahmeDisplay ?? false,
    RechnungDisplay: paymentConfig.RechnungDisplay ?? false,
    PayPalDisplay: paymentConfig.PayPalDisplay ?? true,
    PayPalAdvancedCardsDisplay: paymentConfig.PayPalAdvancedCardsDisplay ?? false,
    PayPalGooglePayDisplay: paymentConfig.PayPalGooglePayDisplay ?? false,
    PayPalApplePayDisplay: paymentConfig.PayPalApplePayDisplay ?? false,
    PayPalPayLaterDisplay: paymentConfig.PayPalPayLaterDisplay ?? false,
    DemoPaymentDisplay: paymentConfig.DemoPaymentDisplay ?? false,

  /* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
  
  onlyStockWarningLimit: 10,
  stockFullQty: 10,
  productInquiry: productInquiryConfig,

  /* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
  /*                               API-Server                              */
  /* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

  // Der API-Server
  api_server: {
    local: readEnv('INTERNAL_API_SERVER_URL', 'http://shops-api:50001'),
    online: !dev
      ? `https://${process.env.NEXT_PUBLIC_HOST}/api`
      : readEnv('INTERNAL_API_SERVER_URL', 'http://shops-api:50001')
  },

  // Image-Server
  image_server: !dev
    ? `https://${process.env.NEXT_PUBLIC_HOST}/image`
    : readEnv('IMAGE_SERVER_INTERNAL_URL', 'http://shops-image:50002')
  ,

  googleRecaptchaKey: '6LfM41klAAAAAIGni4UIvJnM65mm0L8YfQT_PFMc',

  /* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
  /*                                Caching                                */
  /* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

  cache_ssr_products_size:    1000,      // Tausend
  cache_static_products_size: 1_000_000, // Eine Millon

  cache_ssr_menues_size:    100,   // Hundert
  cache_static_menues_size: 1_000, // Tausend

  cache_ssr_brands_size:    100,   // Hundert
  cache_static_brands_size: 1_000, // Tausend

  /* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
  /*                             Datum-Formate                             */
  /* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

  dateShort: {
    year: 'numeric', month: '2-digit', day: '2-digit'
  },

  dateLong: {
    year: 'numeric', month: 'long', day: '2-digit'
  },

  dateLongWeekday: {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  },

  onlyTime: {
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  },

  dayTime: {
    month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit'
  },

  dateTime: {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit'
  },

  dateTimeMiddle: {
    weekday: 'long', year: 'numeric', month: '2-digit', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  },

  dateTimeLong: {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  },

  /* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
  /*                         Zahlen-Formate (Numeral)                      */
  /* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

  Numeral: {
    delimiters: {
      thousands: '.',
      decimal: ','
    },
    abbreviations: {
      thousand: 'k',
      million: 'm',
      billion: 'b',
      trillion: 't'
    },
    currency: {
      symbol: '€'
    }
  },


  /* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
  /*                                 PayPal                                */
  /* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

  paypalConfig,

  /* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

} as const

/* ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
/* ############################################################################################## */
